/**
 * SkipLink (T018) — the keyboard-accessible skip-to-main-content link
 * the shell renders on every route (spec §Accessibility and Mobile
 * Baseline, FR-005: "The shell MUST include a keyboard-accessible
 * skip-to-main-content link on every route"; FR-033: every interactive
 * element MUST present a visible focus indicator meeting WCAG 2.1 AA
 * contrast).
 *
 * **Component shape.** A plain semantic `<a href="#main-content">`. No
 * Radix dependency — a native anchor is already the correct
 * keyboard-and-screen-reader primitive for in-page navigation. T021's
 * `Layout.tsx` will give the `<main>` landmark an `id="main-content"`
 * so activating the link moves focus there; this component does NOT
 * own that target — it only references it by the agreed anchor name.
 *
 * **Copy via `useTranslate`.** The label resolves through the
 * Finnish-first `useTranslate` seam (FR-040) under the key
 * `shell.skipLink.label` — Finnish primary ("Siirry pääsisältöön"),
 * English fallback ("Skip to main content"). The copy is NOT a prop
 * and NOT a hard-coded string; the component reads it internally. If
 * a future caller needs a different skip target or label, the correct
 * fix is a new key in the message bundles or a new sibling component,
 * not a prop-based copy override — the component's contract is "skip
 * to the main landmark", and that contract is stable.
 *
 * **Visibility mechanism.** `sr-only` at rest; `focus:not-sr-only`
 * flips it to visible when keyboard focus lands on the link. This is
 * the canonical skip-link pattern: the link is in the accessibility
 * tree at all times (screen-reader users can navigate to it or skip
 * past it), but it only paints on screen when a sighted keyboard user
 * tabs to it. `focus-visible:not-sr-only` was considered and rejected
 * — `:focus-visible` heuristics vary across engines for anchors (some
 * user-agents suppress the focus-visible flag on anchor clicks that
 * came from keyboard) and the skip-link's whole purpose is to be the
 * first visible interactive element on Tab. `focus` is the unambiguous
 * signal here.
 *
 * **Focus-visible treatment.** Token-backed only (FR-002, FR-047). The
 * visible state paints over the top-left corner of the viewport so it
 * cannot push shell content around: `absolute top-0 left-0` with a
 * small safety margin (`m-2`). Colours come from the primary pair
 * (`bg-primary` / `text-primary-foreground`), the focus ring from the
 * `ring-ring` token the primitive slice uses, plus `ring-offset-2` to
 * match Button/Input/Dialog focus styling. No raw colours, no
 * arbitrary-bracket classes.
 */

import type { JSX } from 'react';

import { useTranslate } from '../i18n/useTranslate.js';

const skipLinkClasses =
  'sr-only focus:not-sr-only ' +
  'focus:fixed focus:top-0 focus:left-0 focus:z-50 focus:m-2 ' +
  'focus:inline-flex focus:items-center focus:rounded-md ' +
  'focus:bg-primary focus:text-primary-foreground focus:px-3 focus:py-2 focus:text-sm focus:font-medium ' +
  'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring';

export function SkipLink(): JSX.Element {
  const t = useTranslate();
  return (
    <a href="#main-content" className={skipLinkClasses}>
      {t('shell.skipLink.label')}
    </a>
  );
}
